import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'

import { isAxiosError } from 'axios'

import { getApiErrorMessage } from '#/shared/api/api-error'
import { Button } from '#/shared/ui/button'
import { Checkbox } from '#/shared/ui/checkbox'
import { Input } from '#/shared/ui/input'
import { PasswordInput } from '#/shared/ui/password-input'
import { useLogin } from './api'
import { AUTH_CARD_CLASS, AuthCardHeader } from './auth-screen'
import { PasskeySignIn } from './passkey-sign-in'
import { apiErrorCode, usePasskeySignIn } from './passkeys'
import { loginSchema, type LoginValues } from './schema'

/**
 * What to tell someone whose sign-in was refused.
 *
 * The API answers a wrong password with the platform's generic 401 body —
 * "Missing or invalid access token" — because a shared handler maps every 401
 * to one locale key and drops the service's own wording. On this form that
 * reads as a bug in the console rather than a typo in the password, so the one
 * status that can only mean bad credentials here gets said plainly. Once
 * passkeys are required, a right password for an account that has one is
 * refused too; the passkey prompt opens by itself then, and the button below
 * is there for a browser that will not open it unasked. Everything else still
 * shows what the API sent.
 */
function signInError(error: unknown): string {
  if (apiErrorCode(error) === 'PASSKEY_REQUIRED') {
    return 'This account signs in with a passkey. Confirm it in the prompt, or press the button below.'
  }
  if (isAxiosError(error) && error.response?.status === 401) {
    return 'Wrong email or password.'
  }
  return getApiErrorMessage(error)
}

/**
 * Both ways in, and the step between them.
 *
 * A password the API refuses with `PASSKEY_REQUIRED` was right, but this
 * account signs in with a passkey: the passkey prompt opens at once instead of
 * leaving the admin to find the button. A browser that will not open it
 * unasked (Safari wants a click) just leaves the message and the button.
 */
function useSignIn() {
  const navigate = useNavigate()
  const login = useLogin()
  const passkey = usePasskeySignIn()
  const enter = () => void navigate({ to: '/' })
  const withPasskey = (remember: boolean) => passkey.mutate({ remember }, { onSuccess: enter })

  const withPassword = (values: LoginValues) =>
    login.mutate(values, {
      onSuccess: (data) => {
        void navigate({ to: 'setup_required' in data ? '/login/passkey' : '/' })
      },
      onError: (error) => {
        if (apiErrorCode(error) === 'PASSKEY_REQUIRED') withPasskey(values.remember)
      },
    })

  return { login, passkey, enter, withPassword, withPasskey }
}

export function LoginForm() {
  const { login, passkey, enter, withPassword, withPasskey } = useSignIn()

  const {
    watch,
    setValue,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', remember: false },
  })

  const onSubmit = handleSubmit(withPassword)

  return (
    <form onSubmit={(event) => void onSubmit(event)} noValidate className={AUTH_CARD_CLASS}>
      <AuthCardHeader title="Sign in" subtitle="Use your platform administrator account" />

      <div className="flex flex-col gap-4">
        <Input
          label="Email"
          type="email"
          autoComplete="username webauthn"
          autoFocus
          placeholder="you@thecargo.io"
          error={Boolean(errors.email)}
          {...register('email')}
        />
        {errors.email ? (
          <p role="alert" className="text-label text-red-500">
            {errors.email.message}
          </p>
        ) : null}

        <PasswordInput
          label="Password"
          autoComplete="current-password"
          placeholder="••••••••"
          error={Boolean(errors.password)}
          {...register('password')}
        />
        {errors.password ? (
          <p role="alert" className="text-label text-red-500">
            {errors.password.message}
          </p>
        ) : null}

        <label className="flex items-center gap-2 text-sm text-gray-900">
          <Checkbox
            checked={watch('remember')}
            onCheckedChange={(next) => setValue('remember', next === true)}
          />
          Keep me signed in
        </label>
      </div>

      {login.isError ? (
        <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-500">
          {signInError(login.error)}
        </p>
      ) : null}

      <Button type="submit" size="lg" loading={login.isPending} className="w-full">
        Sign in
      </Button>
      <PasskeySignIn
        passkey={passkey}
        busy={login.isPending}
        remember={() => watch('remember')}
        onPasskey={() => {
          login.reset()
          withPasskey(watch('remember'))
        }}
        onSignedIn={enter}
      />
    </form>
  )
}
